// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState } from 'react'
import { Leaf, Lock } from 'lucide-react'

const ACCESS_CODE    = '2022!'
const STORAGE_KEY    = 'wilsapp_access_granted'

function isGranted(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true'
}

interface AccessGateProps {
  children: React.ReactNode
}

export function AccessGate({ children }: AccessGateProps) {
  const [granted,  setGranted]  = useState<boolean>(isGranted)
  const [input,    setInput]    = useState('')
  const [error,    setError]    = useState(false)

  if (granted) return <>{children}</>

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (input === ACCESS_CODE) {
      localStorage.setItem(STORAGE_KEY, 'true')
      setGranted(true)
    } else {
      setError(true)
      setInput('')
    }
  }

  return (
    <div className="min-h-screen bg-sand flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-moss shadow-card">
            <Leaf className="h-7 w-7 text-white" />
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-sand-300 shadow-soft p-8">
          <div className="flex justify-center mb-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage-50">
              <Lock className="h-5 w-5 text-sage-600" />
            </div>
          </div>

          <h1 className="text-lg font-semibold text-ink text-center mb-2">
            WilsApp
          </h1>
          <p className="text-sm text-ink-3 text-center mb-6 leading-relaxed">
            U bent uitgenodigd om een demonstratieversie van WilsApp te bekijken.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="access-code" className="text-sm font-medium text-ink-2">
                Toegangscode
              </label>
              <input
                id="access-code"
                type="password"
                value={input}
                onChange={(e) => { setInput(e.target.value); setError(false) }}
                placeholder="Voer toegangscode in"
                autoComplete="off"
                autoFocus
                className={[
                  'w-full rounded-xl border bg-white px-4 py-3',
                  'text-sm text-ink placeholder:text-ink-4 tracking-widest',
                  'focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent',
                  'transition-colors duration-150',
                  error
                    ? 'border-status-alertBorder'
                    : 'border-sand-300 hover:border-sage-300',
                ].join(' ')}
              />
              {error && (
                <p className="text-xs text-status-alertText" role="alert">
                  Onjuiste toegangscode.
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!input}
              className={[
                'w-full rounded-xl bg-moss text-white text-sm font-medium py-3',
                'transition-all duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 focus-visible:ring-offset-2',
                'disabled:opacity-50 disabled:pointer-events-none',
                'hover:bg-moss-light active:bg-sage-900',
              ].join(' ')}
            >
              Toegang
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-ink-4">
          WilsApp Pilot
        </p>
      </div>
    </div>
  )
}
